// Chakra imports
import {useHistory, useParams} from "react-router-dom";
import {Flex, Grid, useDisclosure} from "@chakra-ui/react";
import React, {useEffect, useRef, useState} from "react";
import Information from "./components/Information";
import Transcript from "./components/Transcript";
import AudioRecorderBar from "./components/AudioRecorderBar";
import {startTranscription} from "../../../api/juvolyService";
import {getConversation} from "../../../api/lawdiskService";
import FreemiumExpiredModal from "../DefaultRefactor/Components/FreemiumExpiredModal";
import PreventLeavePageModel from "./components/PreventLeavePageModal";

function RealtimeTranscribe() {
  const isMounted = useRef(false);
  const {isOpen, onOpen, onClose} = useDisclosure();

  const streamControllerRef = useRef(null);

  const [recordingInformation, setRecordingInformation] = useState(null);
  const {id} = useParams();
  const [mediaStream, setMediaStream] = useState(null);
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [sessionStorage, setSessionStorage] = useState({});
  const [completedConversation, setCompletedConversation] = useState([]);
  const [conceptSentence, setConceptSentence] = useState(undefined);
  const [conversationInfo, setConversationInfo] = useState(undefined);
  const [conversationIsPaused, setConversationIsPaused] = useState(false);
  const [conversationIsStopped, setConversationIsStopped] = useState(false);
  const [selectedMicrophone, setSelectedMicrophone] = useState(undefined);
  const [preventLeavePage, setPreventLeavePage] = useState(false);
  const unblockRef = useRef(null);

  const history = useHistory();

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function GetAvailableMicrophones() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const microphones = devices.filter(device => device.kind === 'audioinput');
    setAvailableMicrophones(microphones);
  }

  async function startRealtimeTranscribe() {
    if (!streamControllerRef.current) {
      streamControllerRef.current = await startTranscription({
        mediaStream: mediaStream,
        model: sessionStorage.model,
        onWsMessage: (data) => {
          if (!isMounted.current) return;

          if (data.completed) {
            setCompletedConversation(prev => [...prev, data]);
            setConceptSentence("")
          } else if (!data.completed) {
            setConceptSentence(data);
          }
        },
        onWsError: (err) => {
          streamControllerRef.current?.close();
          streamControllerRef.current = null;
          startRealtimeTranscribe();
        }
      });
    }
  }

  async function ChangeMicrophone(micId) {
    streamControllerRef.current?.stop();
    streamControllerRef.current = null;

    setSelectedMicrophone(micId);
    const updatedSessionStorage = {
      ...sessionStorage,
      microphoneId: micId
    }
    setSessionStorage(updatedSessionStorage);

    await GetMediaStream(micId);
    localStorage.setItem(id, JSON.stringify(updatedSessionStorage));
  }

  function GetSelectedMicrophone() {
    const st = localStorage.getItem(id);
    if (st !== null) {
      const serializedSessionStorage = JSON.parse(st);
      setSessionStorage(serializedSessionStorage);
      setSelectedMicrophone(serializedSessionStorage.microphoneId);
    }
  }

  async function GetMediaStream(deviceId) {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? {exact: deviceId} : undefined
      },
    });
    setMediaStream(mediaStream);
    return mediaStream;
  }

  async function GetConversation() {
    const conversation = await getConversation(id);
    setConversationInfo(conversation);
  }


  useEffect(() => {
    const recordingInfo = localStorage.getItem(id);
    setRecordingInformation(JSON.parse(recordingInfo));

    GetSelectedMicrophone();
    GetAvailableMicrophones();

    GetConversation();
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };

  }, []);

  useEffect(() => {
    async function StartMediaStream() {
      if (recordingInformation?.microphoneId !== undefined) {
        await GetMediaStream(recordingInformation.microphoneId);
      }
    }

    StartMediaStream()
  }, [recordingInformation])


  useEffect(() => {

    if (mediaStream !== null && recordingInformation !== {}) {
      // Comment to save costs
      startRealtimeTranscribe();
    }
  }, [mediaStream, recordingInformation]);

  // Prevent navigation within the app during recording
  useEffect(() => {
    if (mediaStream !== null) {
      unblockRef.current = history.block((tx) => {
        setPreventLeavePage(true);
        return false;
      });
    } else if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [mediaStream, history]);

  useEffect(() => {
    if (preventLeavePage === true) {
      onOpen();
    }
  }, [preventLeavePage])

  return (
    <Flex direction="column" pt={{sm: "125px", lg: "75px"}}>
      <Grid
        templateColumns={{sm: "1fr", lg: "1fr"}}
        gap="5px"
        mb="15px"
      >
        <AudioRecorderBar
          microphones={availableMicrophones}
          selectedMic={selectedMicrophone}
          onSelectMic={async (value) => {
            await ChangeMicrophone(value)
          }}
          onPause={() => setConversationIsPaused((prev) => {
            mediaStream.getAudioTracks().forEach((track) => (track.enabled = prev));
            return !prev;
          })}
          onStop={async () => {
            setConversationIsStopped(true)
            streamControllerRef.current?.stop();
            mediaStream?.getTracks().forEach((track) => track.stop());
            setMediaStream(null);
            localStorage.removeItem(id);

            await sleep(1500)

            const path = `/admin/dashboard/detail/${id}`;
            history.push(path);
          }}
          isRecording={true}
          isPaused={conversationIsPaused}
          isStopped={conversationIsStopped}
          mediaStream={mediaStream}
          conversation={conversationInfo}
        />
      </Grid>
      <Grid
        templateColumns={{sm: "1fr", lg: "1.85fr 1fr", xl: "70% 30%"}}
        gap="24px"
      >
        <Transcript
          completedConversation={completedConversation}
          conceptSentence={conceptSentence}
          conversation={conversationInfo}
          isStopped={conversationIsStopped}
        />
        <Flex direction="column">
          <Information
            conversation={conversationInfo}
          />
        </Flex>
      </Grid>
      <PreventLeavePageModel isOpen={isOpen} onClose={() => {
        setPreventLeavePage(prev => !prev);
        onClose();
      }} opOpen={onOpen}/>
    </Flex>
  );
}

export default RealtimeTranscribe;
